import React from "react";
import useSWR from "swr";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BadgeIndianRupee, Send } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { api, fmtINR, fmtINRcompact } from "@/lib/api";
import { Skeleton, ProgressRing, StatCard } from "@/components/Atoms";

const fetcher = (u) => api.get(u).then((r) => r.data);

export default function MgmtCollections() {
  const { data } = useSWR("/management/collections", fetcher);
  if (!data) return <Skeleton h={400}/>;

  const chartData = data.projects.map(p => ({ name: p.project_name, demand: p.total_demand / 1e7, paid: p.total_paid / 1e7 }));

  const sendReminder = async (cf, name) => {
    try {
      await api.post("/tickets", {
        customer_flat_id: cf,
        category: "Accounts & Billing",
        priority: "P3",
        subject: `Payment reminder for ${name}`,
        description: `Friendly reminder regarding outstanding payment. Please reach out to your RM to discuss.`,
      });
      toast.success("Reminder sent — ticket created");
    } catch { toast.error("Failed to send reminder"); }
  };

  return (
    <div className="space-y-5">
      <div>
        <div className="rc-title-1 flex items-center gap-2"><BadgeIndianRupee className="text-[var(--rc-blue)]"/> Collections</div>
        <div className="rc-callout">Per-project demand vs collection efficiency.</div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rc-card rc-card-padded" data-testid="coll-demand">
          <div className="rc-caption uppercase tracking-wider">Grand Demand</div>
          <div className="mt-1 rc-tabular sm:hidden" style={{ fontSize: 22, fontWeight: 700 }}>{fmtINRcompact(data.grand_demand)}</div>
          <div className="mt-1 rc-tabular hidden sm:block" style={{ fontSize: 26, fontWeight: 700 }}>{fmtINR(data.grand_demand)}</div>
        </div>
        <div className="rc-card rc-card-padded" data-testid="coll-paid">
          <div className="rc-caption uppercase tracking-wider">Grand Collected</div>
          <div className="mt-1 rc-tabular sm:hidden" style={{ fontSize: 22, fontWeight: 700, color: "var(--rc-success)" }}>{fmtINRcompact(data.grand_paid)}</div>
          <div className="mt-1 rc-tabular hidden sm:block" style={{ fontSize: 26, fontWeight: 700, color: "var(--rc-success)" }}>{fmtINR(data.grand_paid)}</div>
        </div>
        <div className="rc-card rc-card-padded" data-testid="coll-out">
          <div className="rc-caption uppercase tracking-wider">Grand Outstanding</div>
          <div className="mt-1 rc-tabular sm:hidden" style={{ fontSize: 22, fontWeight: 700, color: "var(--rc-orange)" }}>{fmtINRcompact(data.grand_outstanding)}</div>
          <div className="mt-1 rc-tabular hidden sm:block" style={{ fontSize: 26, fontWeight: 700, color: "var(--rc-orange)" }}>{fmtINR(data.grand_outstanding)}</div>
        </div>
      </div>

      <div className="rc-card rc-card-padded">
        <div className="rc-title-3 mb-3">Per project (₹ crores)</div>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E5EA"/>
            <XAxis dataKey="name" stroke="#8E8E93" fontSize={10}/>
            <YAxis stroke="#8E8E93" fontSize={10}/>
            <Tooltip contentStyle={{ borderRadius: 12, border: "none", boxShadow: "0 4px 16px rgba(0,0,0,0.1)" }} formatter={(v) => `${Number(v).toFixed(1)} Cr`}/>
            <Bar dataKey="demand" fill="#0F4B7F" name="Demand" radius={[6, 6, 0, 0]}/>
            <Bar dataKey="paid"   fill="#34C759" name="Collected" radius={[6, 6, 0, 0]}/>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {data.projects.map(p => (
        <div key={p.project_id} className="rc-card rc-card-padded">
          <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
            <div className="min-w-0">
              <div className="rc-title-3">{p.project_name}</div>
              <div className="rc-footnote">{p.location}</div>
            </div>
            <div className="flex items-center gap-3 sm:gap-4 flex-shrink-0">
              <ProgressRing value={Math.min(100, p.efficiency_pct)} size={64} stroke={8}/>
            </div>
          </div>

          {/* Stats: stack vertically on mobile, horizontal on tablet+ */}
          <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-3">
            <div>
              <div className="rc-caption uppercase" style={{ fontSize: 10 }}>Demand</div>
              <div className="rc-headline rc-tabular sm:hidden">{fmtINRcompact(p.total_demand)}</div>
              <div className="rc-headline rc-tabular hidden sm:block">{fmtINR(p.total_demand)}</div>
            </div>
            <div>
              <div className="rc-caption uppercase" style={{ fontSize: 10 }}>Collected</div>
              <div className="rc-headline rc-tabular sm:hidden" style={{ color: "var(--rc-success)" }}>{fmtINRcompact(p.total_paid)}</div>
              <div className="rc-headline rc-tabular hidden sm:block" style={{ color: "var(--rc-success)" }}>{fmtINR(p.total_paid)}</div>
            </div>
            <div>
              <div className="rc-caption uppercase" style={{ fontSize: 10 }}>Outstanding</div>
              <div className="rc-headline rc-tabular sm:hidden" style={{ color: "var(--rc-orange)" }}>{fmtINRcompact(p.outstanding)}</div>
              <div className="rc-headline rc-tabular hidden sm:block" style={{ color: "var(--rc-orange)" }}>{fmtINR(p.outstanding)}</div>
            </div>
          </div>

          {p.customers_with_outstanding.length > 0 && (
            <div className="overflow-x-auto -mx-5 px-5">
              <table className="w-full text-left" style={{ minWidth: 540 }}>
                <thead><tr className="rc-caption uppercase">
                  <th className="py-2 pr-3 whitespace-nowrap">Customer</th><th className="py-2 pr-3 whitespace-nowrap">Flat</th>
                  <th className="py-2 pr-3 text-right whitespace-nowrap">Outstanding</th><th className="py-2 pr-3"></th>
                </tr></thead>
                <tbody>
                  {p.customers_with_outstanding.slice(0, 10).map(c => (
                    <tr key={c.customer_id} className="border-t border-[var(--rc-line)]">
                      <td className="py-2 pr-3"><Link to={`/crm/customers/${c.customer_id}`} className="rc-headline hover:underline">{c.customer_name}</Link><div className="rc-caption whitespace-nowrap">{c.primary_phone}</div></td>
                      <td className="py-2 pr-3 whitespace-nowrap">{c.flat_number}</td>
                      <td className="py-2 pr-3 text-right rc-tabular whitespace-nowrap" style={{ color: "var(--rc-orange)" }}>
                        <span className="sm:hidden">{fmtINRcompact(c.outstanding)}</span>
                        <span className="hidden sm:inline">{fmtINR(c.outstanding)}</span>
                      </td>
                      <td className="py-2 pr-3 text-right whitespace-nowrap">
                        <button onClick={() => sendReminder(c.customer_flat_id, c.customer_name)} className="rc-btn rc-btn-sm rc-btn-secondary"><Send size={14}/> Reminder</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
